"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { SmartImage } from "@/components/SmartImage";
import { FigmaProductCard, FigmaStars } from "@/components/home/FigmaHome";
import { formatTL } from "@/lib/format";
import { useCart } from "@/hooks/useCart";
import { trackAddToCart } from "@/lib/analytics";
import { products, getProductImages, type Product } from "@/lib/products";
import { getAverageRating, getFallbackReviewStats, type Review } from "@/lib/reviews";
import { unsplash } from "@/lib/unsplash";
import { ReviewForm } from "@/components/product/ReviewForm";

/* ------------------------------------------------------------------ */
/* Ürün metinleri (Figma'daki lorem blokların yerine gerçek içerik)     */
/* ------------------------------------------------------------------ */

export const PRODUCT_HIGHLIGHTS: Record<string, string[]> = {
  "qnail-silk-cat-eye": [
    "İpek efektli yansıma — kadifemsi 3D derinlik",
    "Tek katta zengin renk kapatıcılığı",
    "Soyulma ve çatlamaya dayanıklı, haftalarca kalıcı",
    "Mıknatıs ile 5-10 saniyede desen verme",
  ],
  "qnail-builder-gel-50ml": [
    "50 ml — uzun süreli kullanım",
    "Kıvamlı yapı ile mükemmel kontrol",
    "Akmayan formül",
    "UV/LED uyumlu, NO:13 doğal pembe",
  ],
  "qnail-soft-gel-tips-oval-medium-550": [
    "550 adet, çok beden",
    "Yeni nesil soft jel tips",
    "Oval medium, french uyumlu",
    "Esnek ve kırılmaya dayanıklı",
  ],
  "uno-nail-prep-dehydrator": [
    "50 ml dehydrator",
    "Yağ ve nemi giderir",
    "Kalıcılığı artırır",
    "Protez öncesi hazırlık",
  ],
  "uno-diamond-shield-top-coat": [
    "50 ml top coat",
    "Elmas parlaklığı",
    "Sararma yapmaz",
    "UV/LED uyumlu",
  ],
  "qnail-cat-eye-magnet-6li": [
    "6'sı bir arada magnet seti",
    "Kedi gözü efekti",
    "Tüm cat-eye ojelerle uyumlu",
    "Profesyonel sonuç",
  ],
  "uno-gel-polish-bronz-12ml": [
    "12 ml kalıcı oje",
    "Metalik bronz",
    "UV/LED uyumlu",
    "Tek katta opak, yoğun pigment",
  ],
  "qnail-mbqn-tips-glue-7gr": [
    "7 gr fırçalı yapıştırıcı",
    "Tips ve protez uyumlu",
    "Güçlü tutuş",
    "Kolay uygulama",
  ],
  "qnail-french-stamper": [
    "French ve desen damgası",
    "Hassas desen aktarımı",
    "Yeniden kullanılabilir",
    "Başlangıç dostu",
  ],
  "qnail-sculpture-jel-beyaz-02-15gr": [
    "15 gr sculpture hamur jel",
    "Beyaz NO:02",
    "Şekillendirme dostu",
    "Akmayan kıvam",
  ],
  "qnail-inci-tozu-pigment": [
    "Gri-yeşil inci pigmenti",
    "Çift uçlu aplikatör dahil",
    "Nail art detayları",
    "Sedefli ışıltılı bitiş",
  ],
  "qnail-uno-elastik-base-15ml": [
    "50 ml elastik base",
    "Esnek alt kat",
    "Kırılmayı önler",
    "Kalıcı oje bazı",
  ],
  "qnail-uno-super-black-15ml": [
    "50 ml siyah kalıcı oje",
    "Tek katta opak",
    "Yoğun pigment",
    "UV/LED uyumlu",
  ],
  "qnail-uno-magic-remover": [
    "Kalıcı oje çıkarıcı",
    "Tırnağı yormaz",
    "Hızlı çözünme",
    "Pratik kullanım",
  ],
  "qnail-uno-strong-base-50ml": [
    "50 ml strong base",
    "Güçlü tutuş",
    "Profesyonel boy",
    "Uzun süre kullanım",
  ],
};

export const PRODUCT_DETAILS: Record<string, string> = {
  "qnail-silk-cat-eye": "Hazırlık: Tırnağı törpüleyip primer uygulayın. İnce kat Baz Kat sürüp UV/LED lambada kurutun. İsteğe bağlı koyu zemin rengi sürün. QNail İpek Efektli Ojeyi uygulayıp kurutmadan önce mıknatısı 0.5 cm mesafede 5-10 saniye tutarak deseni verin. UV lambada 60 saniye (LED\u0027de 30 saniye) kurutup Üst Kat ile kapatın. Ürün Tipi: Manyetik Cat Eye Kalıcı Jel Oje. Efekt: İpek (Silk). Gerekli Ekipman: UV/LED lamba ve mıknatıs.",
  "qnail-builder-gel-50ml":
    "Qnail Builder Gel No:13, profesyonel protez tırnak uygulamaları için özel olarak formüle edilmiş bir jeldir. 50 ml'lik hacmi ile uzun süreli kullanım imkânı sunarken, kıvamlı yapısı sayesinde uygulama sırasında mükemmel bir kontrol sağlar.\n\nBu ürün, akmayan özelliği ile dikkat çeker. Bu sayede uygulama sırasında istenmeyen hataların önüne geçilir ve sonuçlar her zaman profesyonel bir görünüm kazanır. Qnail Builder Gel No:13, tırnakların doğal yapısını koruyarak dayanıklı ve estetik bir görünüm elde edilmesine olanak tanır.\n\nYüksek viskoziteye sahip bu jel, hem uzatma hem de şekillendirme işlemlerinde mükemmel bir performans sergiler. Kullanıcı dostu formülü hem deneyimli profesyoneller hem de yeni başlayanlar için idealdir.",
};

/* ------------------------------------------------------------------ */
/* Galeri                                                              */
/* ------------------------------------------------------------------ */

export function PdpGallery({ product }: { product: Product }) {
  const images = getProductImages(product);
  const [index, setIndex] = useState(0);
  const go = (dir: 1 | -1) => setIndex((i) => (i + dir + images.length) % images.length);

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="relative aspect-square w-full overflow-hidden rounded-[32px] bg-White outline outline-1 outline-offset-[-1px] outline-Background">
        <SmartImage
          key={images[index]}
          src={images[index]}
          fallback={unsplash.cosmetics(1000, 1000)}
          alt={product.name}
          fill
          priority
          className="object-cover"
          sizes="(max-width: 1024px) 100vw, 45vw"
        />
      </div>
      {images.length > 1 && (
        <div className="flex w-full flex-col gap-3">
          <div className="flex items-start gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {images.map((src, i) => (
              <button
                key={src + i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Görsel ${i + 1}`}
                aria-pressed={i === index}
                className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-Background-light outline outline-1 outline-offset-[-1px] transition sm:h-24 sm:w-24 ${i === index ? "outline-2 outline-Primary" : "outline-Background hover:outline-Primary-light"}`}
              >
                <SmartImage src={src} fallback={unsplash.cosmetics(200, 200)} alt="" fill className="object-cover" sizes="96px" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Önceki görsel"
              className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark opacity-60 backdrop-blur-[10px] transition hover:opacity-100"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Sonraki görsel"
              className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark backdrop-blur-[10px] transition hover:bg-white/40"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mini sepete ekle (birlikte al satırları)                             */
/* ------------------------------------------------------------------ */

function MiniAddButton({ product }: { product: Product }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        addItem({ slug: product.slug, name: product.name, price: product.price, quantity: 1, image: product.image });
        trackAddToCart({ id: product.slug, name: product.name, price: product.price, quantity: 1 });
        setAdded(true);
        window.setTimeout(() => setAdded(false), 1600);
      }}
      className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-full px-4 py-2 text-sm font-medium uppercase leading-5 text-Primary outline outline-1 outline-Primary transition hover:bg-Primary hover:text-White active:scale-[0.98]"
    >
      {added ? "Eklendi" : (<><span className="sm:hidden">+ Ekle</span><span className="hidden sm:inline">Sepete Ekle</span></>)}
    </button>
  );
}

export function BirlikteAl({ items }: { items: Product[] }) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, 3);
  return (
    <div className="flex w-full flex-col items-center gap-3 py-6">
      <div className="flex w-full items-start gap-2.5 self-stretch border-b border-Background pb-2">
        <h3 className="text-center text-2xl font-medium leading-7 text-Primary">Birlikte Alabileceğiniz Ürünler</h3>
      </div>
      {shown.map((p) => (
        <div key={p.slug} className="flex w-full items-center gap-4 border-b border-Background pb-6">
          <Link href={`/urunler/${p.slug}`} className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-Background-light sm:h-28 sm:w-28" aria-label={p.name}>
            <SmartImage src={p.image} fallback={unsplash.cosmetics(200, 200)} alt="" fill className="object-cover" sizes="112px" />
          </Link>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <div className="flex flex-col items-start gap-0.5">
              <p className="line-clamp-2 text-base font-medium leading-6 text-800 sm:text-lg sm:leading-7">
                <Link href={`/urunler/${p.slug}`} className="transition hover:text-Primary">{p.name}</Link>
              </p>
              <p className="line-clamp-1 text-sm font-normal leading-5 text-500 sm:text-base sm:leading-6">{p.description}</p>
            </div>
            <p className="text-base font-bold leading-6 text-Primary sm:text-lg sm:leading-7">{formatTL(p.price)}</p>
          </div>
          <MiniAddButton product={p} />
        </div>
      ))}
      {items.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center justify-center rounded-full px-6 py-3 text-base font-medium uppercase leading-6 text-Primary transition hover:bg-Background-light"
        >
          {expanded ? "Daha az göster" : "Daha fazlasını görüntüle"}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Paylaş                                                              */
/* ------------------------------------------------------------------ */

export function ShareButton({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        } catch {
          setCopied(false);
        }
      }}
      aria-label={`${title} bağlantısını kopyala`}
      className="flex h-6 w-6 items-center justify-center text-Primary-dark transition hover:text-Primary"
    >
      {copied ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-emerald-600" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" /><line x1="15.4" y1="6.5" x2="8.6" y2="10.5" /></svg>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* SSS                                                               */
/* ------------------------------------------------------------------ */

export function FaqSection({ faqs }: { faqs: { q: string; a: string }[] }) {
  if (faqs.length === 0) return null;
  return (
    <section aria-label="Sıkça sorulan sorular" className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-10 sm:px-8 sm:py-14 lg:px-12">
      <h2 className="pb-5 text-center text-2xl font-semibold leading-tight tracking-tight text-800 sm:pb-6 sm:text-4xl sm:leading-[56px]">
        Sıkça Sorulan Sorular
      </h2>
      <div className="flex w-full max-w-[1200px] flex-col items-stretch gap-2.5 sm:gap-3">
        {faqs.map((f, i) => (
          <details
            key={f.q}
            open={i === 0}
            className="group w-full rounded-3xl bg-White outline outline-1 outline-offset-[-1px] outline-Background-dark transition open:bg-Background-light open:shadow-sm"
          >
            <summary className="flex min-h-[60px] w-full cursor-pointer list-none items-center gap-3 p-4 pr-4 text-left text-[15px] font-medium leading-6 text-800 transition sm:min-h-[72px] sm:gap-4 sm:p-6 sm:text-lg sm:leading-7 [&::-webkit-details-marker]:hidden [&::marker]:content-none">
              <span className="min-w-0 flex-1">{f.q}</span>
              <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-Primary p-2 text-White transition-transform duration-300 group-open:rotate-180 sm:h-9 sm:w-9"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
              </span>
            </summary>
            <div className="px-4 pb-5 sm:px-6 sm:pb-6">
              <p className="border-t border-Background-dark/60 pt-3 text-sm font-normal leading-6 text-Black sm:pt-4 sm:text-base">
                {f.a}
              </p>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Yorumlar                                                            */
/* ------------------------------------------------------------------ */

function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

export function PdpReviews({ slug, reviews, summary }: { slug: string; reviews: Review[]; summary?: { avg: number; count: number } }) {
  const real = summary ?? getAverageRating(slug);
  // Gerçek yoksa slug'a özel fallback göster — hep aynı sayı durmasın
  const fb = getFallbackReviewStats(slug);
  const hasReal = real.count > 0;
  const avg = hasReal ? real.avg : fb.avg;
  const count = hasReal ? real.count : fb.count;
  const [query, setQuery] = useState("");
  const [minRating, setMinRating] = useState<number | null>(null);
  const [sort, setSort] = useState<"new" | "high" | "low">("new");
  const [visible, setVisible] = useState(3);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("tr");
    let list = reviews.filter(
      (r) =>
        (!needle || r.comment.toLocaleLowerCase("tr").includes(needle) || r.author.toLocaleLowerCase("tr").includes(needle)) &&
        (minRating === null || r.rating >= minRating)
    );
    list = [...list].sort((a, b) => {
      if (sort === "high") return b.rating - a.rating;
      if (sort === "low") return a.rating - b.rating;
      return b.date.localeCompare(a.date);
    });
    return list;
  }, [reviews, query, minRating, sort]);

  const shown = filtered.slice(0, visible);

  return (
    <section aria-label="Müşteri yorumları" className="mx-auto flex w-full max-w-[1200px] flex-col items-start gap-6 px-4 sm:px-8 lg:px-0">
      <h2 className="w-full text-center text-3xl font-semibold leading-tight tracking-tight text-800 sm:text-4xl sm:leading-[56px]">
        Yorumlar
      </h2>

      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:gap-12">
        <div className="flex w-40 flex-col items-center gap-2.5 px-4">
          <p className="text-center text-5xl font-normal leading-[48px] text-Black">{count > 0 ? avg : "–"}</p>
          <FigmaStars slug={slug} />
          <p className="text-center text-sm font-normal leading-5 text-Primary">
            {count > 0 ? `${count} değerlendirme` : "Henüz değerlendirme yok"}
          </p>
        </div>
        <div className="flex flex-1 justify-start sm:justify-end">
          <a
            href="#yorum-yaz"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-Primary px-4 py-2 text-sm font-medium uppercase leading-5 text-White transition hover:bg-Primary-dark"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" /></svg>
            Yorum Yaz
          </a>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <form
          role="search"
          aria-label="Yorumlarda ara"
          onSubmit={(e) => e.preventDefault()}
          className="flex h-10 w-full items-center gap-2 rounded-xl bg-Background-light px-4 py-3 outline outline-1 outline-offset-[-1px] outline-Background-dark lg:w-72"
        >
          <label htmlFor="yorum-ara" className="sr-only">Yorumlarda ara</label>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 shrink-0 text-Primary-dark" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input
            id="yorum-ara"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setVisible(3); }}
            placeholder="Ara"
            autoComplete="off"
            className="w-full min-w-0 flex-1 bg-transparent text-sm leading-4 text-zinc-900 placeholder:text-500 focus:outline-none"
          />
        </form>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: "Tümü", value: null },
            { label: "5★", value: 5 },
            { label: "4★+", value: 4 },
            { label: "3★+", value: 3 },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => { setMinRating(o.value); setVisible(3); }}
              aria-pressed={minRating === o.value}
              className={`flex min-h-[44px] items-center rounded-xl px-3 py-2.5 text-sm font-medium uppercase leading-5 outline outline-[1.5px] transition ${minRating === o.value ? "bg-Primary text-White outline-Primary" : "text-Primary outline-Background-dark hover:bg-Background-light"}`}
            >
              {o.label === "Tümü" ? "FİLTRE" : o.label}
            </button>
          ))}
          <details className="relative">
            <summary className="flex h-10 cursor-pointer list-none items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium uppercase leading-5 text-Primary outline outline-[1.5px] outline-Background-dark hover:bg-Background-light [&::-webkit-details-marker]:hidden">
              Sırala: {sort === "new" ? "Yeni" : sort === "high" ? "Puan ↓" : "Puan ↑"}
            </summary>
            <div className="absolute left-0 top-full z-30 mt-2 w-44 max-w-[calc(100vw-2rem)] sm:left-auto sm:right-0 rounded-2xl bg-White p-2 shadow-xl outline outline-1 outline-Background-dark">
              {([["new", "En yeni"], ["high", "Puan yüksek"], ["low", "Puan düşük"]] as const).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setSort(v)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${sort === v ? "bg-Primary font-medium text-White" : "text-800 hover:bg-Background-light"}`}
                >
                  {l}
                  {sort === v && <span aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          </details>
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        {shown.length === 0 ? (
          <p className="w-full rounded-[30px] bg-Background-light p-6 text-center text-sm text-500 outline outline-1 outline-Background">
            {reviews.length === 0
              ? hasReal
                ? "Bu ürün için henüz yorum yok — ilk yorumu sen yaz."
                : `${count} değerlendirme alındı — detaylı yorumlar moderasyondan sonra burada listelenir. İlk yorumu sen yaz.`
              : "Bu filtrede yorum bulunamadı."}
          </p>
        ) : (
          shown.map((r) => (
            <article key={r.id} className="flex w-full flex-col gap-4 rounded-[30px] p-6 outline outline-1 outline-offset-[-1px] outline-Background lg:flex-row">
              <div className="flex w-full flex-col items-start gap-4 rounded-[30px] bg-White lg:w-56 lg:min-w-56">
                <div className="flex flex-col items-start">
                  <p className="text-base font-medium leading-5 text-Black">{r.author}</p>
                  {r.verified && <p className="text-xs font-normal leading-5 text-Primary-light">Doğrulanmış Alıcı</p>}
                  {r.rating >= 4 && (
                    <p className="mt-1 inline-flex items-center gap-1 rounded-3xl py-0.5 pl-1 pr-2 outline outline-1 outline-offset-[-1px] outline-Primary">
                      <span className="flex items-center justify-center rounded-full bg-Tertiary p-0.5" aria-hidden="true">
                        <svg viewBox="0 0 24 24" className="h-3 w-3 text-White" fill="currentColor" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                      </span>
                      <span className="text-[10px] font-semibold leading-6 text-Primary">TAVSİYE EDİYORUM</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-start rounded-[30px] bg-White">
                <div className="flex w-full items-center justify-between gap-2">
                  <FigmaStars slug={slug} />
                  <p className="shrink-0 text-sm font-normal leading-5 text-Black">{timeLabel(r.date)}</p>
                </div>
                <p className="w-full pt-1.5 text-sm font-normal leading-5 text-Black sm:text-base sm:leading-6">{r.comment}</p>
                {r.photo && (
                  <span className="relative mt-4 block h-24 w-24 overflow-hidden rounded-2xl bg-Background-light outline outline-1 outline-Background">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={r.photo} alt={`${r.author} kullanıcı fotoğrafı`} loading="lazy" className="h-full w-full object-cover" />
                  </span>
                )}
              </div>
            </article>
          ))
        )}
      </div>

      {visible < filtered.length && (
        <div className="flex w-full flex-col items-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + 3)}
            className="inline-flex items-center justify-center rounded-full px-6 py-3 text-base font-medium uppercase leading-6 text-Primary transition hover:bg-Background-light"
          >
            Daha fazla yorum görün
          </button>
        </div>
      )}

      <ReviewForm slug={slug} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Son görüntülenenler                                                 */
/* ------------------------------------------------------------------ */

const RECENT_KEY = "onomac-recent";

function subscribeRecent(cb: () => void) {
  window.addEventListener("storage", cb);
  return () => window.removeEventListener("storage", cb);
}

function getRecentSnapshot(): string {
  try {
    return localStorage.getItem(RECENT_KEY) ?? "[]";
  } catch {
    return "[]";
  }
}

export function RecentlyViewed({ current, fallback }: { current: Product; fallback: Product[] }) {
  // Kaydet — yalnızca yazar, state güncellemesi yok
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const slugs: string[] = raw ? JSON.parse(raw) : [];
      const next = [current.slug, ...slugs.filter((s) => s !== current.slug)].slice(0, 8);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* yoksay */
    }
  }, [current.slug]);

  const snapshot = useSyncExternalStore(subscribeRecent, getRecentSnapshot, () => "[]");
  const list = useMemo(() => {
    let parsed: string[] = [];
    try {
      const v: unknown = JSON.parse(snapshot);
      if (Array.isArray(v)) parsed = v.filter((s): s is string => typeof s === "string");
    } catch {
      parsed = [];
    }
    const resolved = parsed
      .filter((s) => s !== current.slug)
      .map((s) => products.find((p) => p.slug === s))
      .filter((p): p is Product => Boolean(p))
      .slice(0, 4);
    return resolved.length > 0 ? resolved : fallback.slice(0, 4);
  }, [snapshot, current.slug, fallback]);

  return (
    <section aria-label="Son görüntülenen ürünler" className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-14 sm:px-6">
      <h2 className="pb-6 text-center text-3xl font-semibold leading-tight tracking-tight text-800 sm:text-4xl sm:leading-[56px]">
        Son görüntülenen ürünler
      </h2>
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {list.map((p) => (
          <FigmaProductCard key={p.slug} product={p} />
        ))}
      </div>
    </section>
  );
}
